import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { StarFilled, StarHalfFilled } from '@withjoy/joykit/icons';
import { Skeleton, SkeletonText } from '@shared/components/Skeleton';
import { formatUserRatingNumber, useReviewCountFormatter } from '@apps/accommodations/AccommodationsConsole.utils';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

const StarRating = ({ rating }: { rating: number }) => {
  const isHalfRating = rating % 1 === 0.5;
  return (
    <Flex columnGap={'2px'}>
      {Array.from({ length: Math.floor(rating) }, (_, index) => (
        <StarFilled
          key={index}
          size={16}
        />
      ))}
      {isHalfRating && <StarHalfFilled size={16} />}
    </Flex>
  );
};

StarRating.Skeleton = () => {
  return (
    <Flex columnGap={'4px'}>
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
    </Flex>
  );
};

const UserRating = ({ rating, reviewCount }: { rating: Maybe<number>; reviewCount: Maybe<number> }) => {
  const { t } = useTranslation('accommodationsConsole');
  const formatReviewCount = useReviewCountFormatter();
  const isMobileOrTablet = useIsMobileOrTablet();
  const label = useMemo(() => {
    if (!rating) {
      return null;
    }
    const categoryTrans = t('listingsPanel', 'userRatings', 'category');
    switch (true) {
      case rating >= 9:
        return categoryTrans.wonderful();
      case rating >= 8.5:
        return categoryTrans.fabulous();
      case rating >= 8:
        return categoryTrans.veryGood();
      case rating >= 7:
        return categoryTrans.good();
      case rating >= 6:
        return categoryTrans.pleasant();
      default:
        return null;
    }
  }, [rating, t]);

  const reviewCountText = useMemo(() => {
    if (!reviewCount) {
      return null;
    }
    return t('listingsPanel', 'userRatings', 'reviewCounts')?.({ reviewCount: formatReviewCount(reviewCount) });
  }, [formatReviewCount, reviewCount, t]);

  return (
    <Flex
      alignItems="center"
      flexDirection={{ _: 'row', md: 'row-reverse' }}
      columnGap={{ _: 3, md: 5 }}
    >
      {typeof rating === 'number' && rating > 0 && (
        <Box
          height={34}
          width="auto"
          borderRadius="6px"
          paddingY={3}
          paddingX={2}
          backgroundColor="newUIBrandPrimary"
          color="white"
          textAlign="center"
        >
          {formatUserRatingNumber(rating)}
        </Box>
      )}
      {(label || (reviewCount && reviewCount > 0)) && (
        <Flex
          flexDirection={{ _: 'row', md: 'column' }}
          alignItems={{ _: 'center', md: 'none' }}
          rowGap={1}
          columnGap={2}
        >
          {label && <TextV2 typographyVariant="button1">{label}</TextV2>}
          {reviewCount && reviewCount > 0 && (
            <TextV2
              typographyVariant="label2"
              whiteSpace="nowrap"
              color={'mono10'}
            >
              {isMobileOrTablet ? `(${reviewCountText})` : reviewCountText}
            </TextV2>
          )}
        </Flex>
      )}
    </Flex>
  );
};

UserRating.Skeleton = () => {
  return (
    <Flex
      alignItems="center"
      flexDirection={{ _: 'row', md: 'row-reverse' }}
      columnGap={{ _: 3, md: 5 }}
    >
      <Skeleton
        height={34}
        width={34}
      />
      <Flex
        flexDirection={{ _: 'row', md: 'column' }}
        alignItems={{ _: 'center', md: 'none' }}
        columnGap={2}
        rowGap={'10px'}
      >
        <SkeletonText
          rows={1}
          width={75}
          skeletonHeight={20}
        />
        <SkeletonText
          rows={1}
          width={75}
          skeletonHeight={14}
        />
      </Flex>
    </Flex>
  );
};

export { UserRating, StarRating };
